import { DownloadOutlined, MessageOutlined } from "@ant-design/icons"
import { Alert, Button, Segmented, Skeleton, Tag } from "antd"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { CardComponent } from "../../components/Card/CardComponent.tsx"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { downloadFile } from "../../utils/download-file.ts"
import { budgetFormat } from "../../utils/number/budget-format.ts"
import { ContactContractorModal, type ContactProjectOption } from "./components/ContactContractorModal.tsx"
import { PortalEmptyState } from "./components/PortalEmptyState.tsx"
import { PortalPageContainer } from "./components/PortalPageContainer.tsx"
import { PortalPageHeader } from "./components/PortalPageHeader.tsx"
import { formatPortalDate } from "./helpers/format-date.ts"
import { resolveGcContact } from "./helpers/recipient.ts"
import { fetchAllProjectsInvoices, fetchPortalInvoices } from "./redux/portal.thunks.ts"
import type { PortalCrossProjectInvoice, PortalInvoice } from "./types/portal.types.ts"

const balanceOf = (invoice: PortalInvoice) =>
  Number(invoice.amount) - Number(invoice.paid_to_date || 0)
const isOutstanding = (invoice: PortalInvoice) => balanceOf(invoice) > 0
const isOverdue = (invoice: PortalInvoice) =>
  isOutstanding(invoice) &&
  !!invoice.due_date &&
  new Date(invoice.due_date).getTime() < Date.now()

// Minimal, real invoice list: status, amount, due date, and the invoice's
// own PDF when it has one. Online payment (FIN 04-06) stays out of scope —
// it is gated on ezbuild_back's Stripe integration, which is disabled
// platform-wide (docs/kb/decisions/2025-05-19-disable-payments-and-subscription-apps.md
// in ezbuild_back), not something this iteration can turn on. "Ask a
// question" and "Send payment confirmation" are not wired for the same
// reason as Messages — see this session's delivery notes.
function PortalPaymentsPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const [filter, setFilter] = useState<"all" | "unpaid">(
    searchParams.get("status") === "unpaid" ? "unpaid" : "all"
  )
  const highlightId = searchParams.get("invoice")
  const highlightRef = useRef<HTMLDivElement | null>(null)
  const [contactProject, setContactProject] = useState<ContactProjectOption | null>(null)

  const selectedProjectId = useAppSelector(
    (state) => state.portal.selectedProjectId
  )
  const projects = useAppSelector((state) => state.portal.projects)
  const projectsLoaded = useAppSelector((state) => state.portal.projectsLoaded)
  const invoices = useAppSelector((state) => state.portal.invoices)
  const fetching = useAppSelector((state) => state.portal.invoicesFetching)
  const failed = useAppSelector((state) => state.portal.invoicesError)
  const crossInvoices = useAppSelector((state) => state.portal.crossProjectInvoices)
  const crossFetching = useAppSelector((state) => state.portal.crossProjectInvoicesFetching)
  const crossLoaded = useAppSelector((state) => state.portal.crossProjectInvoicesLoaded)

  const allProjectsMode = selectedProjectId === null

  useEffect(() => {
    if (selectedProjectId) void dispatch(fetchPortalInvoices(selectedProjectId))
  }, [dispatch, selectedProjectId])

  useEffect(() => {
    if (allProjectsMode && projectsLoaded && projects.length > 1 && !crossLoaded && !crossFetching) {
      void dispatch(
        fetchAllProjectsInvoices(projects.map((p) => ({ id: p.id, lead_name: p.lead_name })))
      )
    }
  }, [allProjectsMode, projectsLoaded, projects, crossLoaded, crossFetching, dispatch])

  const sourceInvoices: (PortalInvoice | PortalCrossProjectInvoice)[] = allProjectsMode
    ? crossInvoices
    : invoices

  useEffect(() => {
    if (highlightId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [highlightId, sourceInvoices])

  const visible = useMemo(
    () => (filter === "unpaid" ? sourceInvoices.filter(isOutstanding) : sourceInvoices),
    [sourceInvoices, filter]
  )

  const askAboutInvoice = (invoice: PortalInvoice | PortalCrossProjectInvoice) => {
    const projectId = allProjectsMode ? (invoice as PortalCrossProjectInvoice).project_id : selectedProjectId
    const project = projects.find((p) => p.id === projectId)
    if (!project) return
    setContactProject({
      id: project.id,
      name: project.lead_name,
      contact: resolveGcContact(project.project_managers) ?? null
    })
  }

  // Per-project summary only — a combined total across projects would sum
  // figures that are never guaranteed to share a currency, so All-projects
  // mode shows the itemized list with no grand-total card instead.
  const summary = useMemo(() => {
    const outstanding = invoices.filter(isOutstanding).reduce((sum, i) => sum + balanceOf(i), 0)
    const overdue = invoices.filter(isOverdue).reduce((sum, i) => sum + balanceOf(i), 0)
    const paid = invoices.reduce((sum, i) => sum + Number(i.paid_to_date || 0), 0)
    return { outstanding, overdue, paid }
  }, [invoices])

  if (!projectsLoaded) {
    return (
      <PortalPageContainer className="max-w-3xl">
        <Skeleton active paragraph={{ rows: 6 }} />
      </PortalPageContainer>
    )
  }

  if (projects.length === 0) {
    return (
      <PortalPageContainer className="max-w-3xl">
        <PortalPageHeader title={t("portal.payments.title")} />
        <PortalEmptyState
          title={t("portal.home.noProjectsTitle")}
          description={t("portal.home.noProjectsDescription")}
        />
      </PortalPageContainer>
    )
  }

  if (!allProjectsMode && fetching && invoices.length === 0) {
    return (
      <PortalPageContainer className="max-w-3xl">
        <Skeleton active paragraph={{ rows: 6 }} />
      </PortalPageContainer>
    )
  }

  if (allProjectsMode && crossFetching && !crossLoaded) {
    return (
      <PortalPageContainer className="max-w-3xl">
        <Skeleton active paragraph={{ rows: 6 }} />
      </PortalPageContainer>
    )
  }

  if (!allProjectsMode && failed) {
    return (
      <PortalPageContainer className="max-w-3xl">
        <Alert
          type="error"
          showIcon
          message={t("portal.payments.loadError")}
          action={
            <Button
              size="small"
              onClick={() => void dispatch(fetchPortalInvoices(selectedProjectId as string))}
            >
              {t("portal.home.retry")}
            </Button>
          }
        />
      </PortalPageContainer>
    )
  }

  return (
    <PortalPageContainer className="flex max-w-3xl flex-col gap-4">
      <PortalPageHeader
        title={t("portal.payments.title")}
        actions={
          <Segmented
            value={filter}
            onChange={(value) => setFilter(value as "all" | "unpaid")}
            options={[
              { label: t("portal.payments.filterAll"), value: "all" },
              { label: t("portal.payments.filterUnpaid"), value: "unpaid" }
            ]}
          />
        }
      />

      {!allProjectsMode && invoices.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <CardComponent size="small">
            <p className="text-xs uppercase text-gray-400">
              {t("portal.payments.summaryOutstanding")}
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {budgetFormat(summary.outstanding)}
            </p>
          </CardComponent>
          <CardComponent size="small">
            <p className="text-xs uppercase text-gray-400">
              {t("portal.payments.summaryOverdue")}
            </p>
            <p
              className={`text-lg font-semibold ${summary.overdue > 0 ? "text-red-600" : "text-gray-900"}`}
            >
              {budgetFormat(summary.overdue)}
            </p>
          </CardComponent>
          <CardComponent size="small">
            <p className="text-xs uppercase text-gray-400">
              {t("portal.payments.summaryPaid")}
            </p>
            <p className="text-lg font-semibold text-gray-900">{budgetFormat(summary.paid)}</p>
          </CardComponent>
        </div>
      )}

      {sourceInvoices.length === 0 ? (
        <PortalEmptyState
          title={t("portal.payments.emptyTitle")}
          description={t("portal.payments.emptyDescription")}
        />
      ) : visible.length === 0 ? (
        <PortalEmptyState title={t("portal.payments.noMatches")} />
      ) : (
        <div className="flex flex-col gap-3">
          {visible.map((invoice) => {
            const balance = balanceOf(invoice)
            const pdf = invoice.attachments?.[0]
            const crossInvoice = allProjectsMode ? (invoice as PortalCrossProjectInvoice) : null
            return (
              <div
                key={invoice.id}
                ref={invoice.id === highlightId ? highlightRef : undefined}
              >
                <CardComponent
                  size="small"
                  className={invoice.id === highlightId ? "ring-2 ring-primary" : undefined}
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {t("portal.payments.invoiceNumber", { number: invoice.invoice_number })}
                      </p>
                      {crossInvoice && (
                        <p className="text-xs font-medium text-primary">
                          {crossInvoice.project_name}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        {t("portal.payments.due")} {formatPortalDate(invoice.due_date) ?? "—"}
                        {" · "}
                        {t("portal.payments.balance")} {budgetFormat(balance)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Tag color={isOverdue(invoice) ? "red" : balance > 0 ? "orange" : "green"}>
                        {invoice.status_display}
                      </Tag>
                      <span className="font-medium text-gray-900">
                        {budgetFormat(invoice.amount)}
                      </span>
                      {pdf && (
                        <Button
                          type="text"
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() =>
                            void downloadFile(pdf.file, `${invoice.invoice_number}.pdf`)
                          }
                          aria-label={t("portal.documents.download")}
                        />
                      )}
                      <Button
                        type="text"
                        size="small"
                        icon={<MessageOutlined />}
                        onClick={() => askAboutInvoice(invoice)}
                        aria-label={t("portal.payments.askQuestion")}
                      />
                    </div>
                  </div>
                </CardComponent>
              </div>
            )
          })}
        </div>
      )}

      <ContactContractorModal
        open={!!contactProject}
        onClose={() => setContactProject(null)}
        project={contactProject ?? undefined}
      />
    </PortalPageContainer>
  )
}

export default PortalPaymentsPage
